import React, { useRef, useState, useCallback } from 'react'
import { Camera, X, RotateCcw, Check, ImagePlus } from 'lucide-react'
import { Button } from '../common'
import { compressImage, blobToBase64 } from '../../utils/helpers'

// Create thumbnail blob from image blob
async function createThumbnailBlob(blob, maxSize = 300) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      let { width, height } = img

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width
          width = maxSize
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height
          height = maxSize
        }
      }

      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      // Fill with white background for JPEG (no transparency)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, width, height)
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (thumbnailBlob) => {
          URL.revokeObjectURL(img.src)
          if (thumbnailBlob) {
            resolve(thumbnailBlob)
          } else {
            reject(new Error('Failed to create thumbnail'))
          }
        },
        'image/jpeg',
        0.8
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('Failed to load image'))
    }
    img.src = URL.createObjectURL(blob)
  })
}

export default function CameraCapture({
  onCapture,
  onCancel,
  selectedCategory
}) {
  const fileInputRef = useRef(null)
  const galleryInputRef = useRef(null)
  const processingRef = useRef(false)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [multiProgress, setMultiProgress] = useState(null) // { current: 1, total: 5 }

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file || processingRef.current) return

    processingRef.current = true
    setError(null)
    setLoading(true)

    try {
      // Compress the image
      const compressedBlob = await compressImage(file)

      // Create thumbnail blob
      const thumbnailBlob = await createThumbnailBlob(compressedBlob)

      // Convert to base64 for preview display
      const imageData = await blobToBase64(compressedBlob)
      const thumbnail = await blobToBase64(thumbnailBlob)

      setPreview({
        imageData,
        thumbnail,
        file: compressedBlob,
        thumbnailBlob
      })
    } catch (err) {
      console.error('Failed to process image:', err)
      setError('Failed to process image. Please try again.')
    } finally {
      setLoading(false)
      processingRef.current = false
    }
  }, [])

  // Handle multiple files from gallery (no preview, direct add)
  const handleGallerySelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    setError(null)
    setLoading(true)

    try {
      // Process files sequentially
      for (let i = 0; i < files.length; i++) {
        setMultiProgress({ current: i + 1, total: files.length })

        const file = files[i]
        const compressedBlob = await compressImage(file)
        const thumbnailBlob = await createThumbnailBlob(compressedBlob)
        const imageData = await blobToBase64(compressedBlob)
        const thumbnail = await blobToBase64(thumbnailBlob)

        // Call onCapture for each photo immediately
        if (onCapture) {
          onCapture({
            imageData,
            thumbnail,
            blob: compressedBlob,
            thumbnailBlob
          })
        }
      }
    } catch (err) {
      console.error('Failed to process images:', err)
      setError('Failed to process some images. Please try again.')
    } finally {
      setLoading(false)
      setMultiProgress(null)
      if (galleryInputRef.current) {
        galleryInputRef.current.value = ''
      }
    }
  }, [onCapture])

  const handleCapture = useCallback(() => {
    if (preview && onCapture) {
      onCapture({
        imageData: preview.imageData,
        thumbnail: preview.thumbnail,
        blob: preview.file,           // Pass blob for R2 upload
        thumbnailBlob: preview.thumbnailBlob  // Pass thumbnail blob
      })
      setPreview(null)
      // Clear file input to ensure onChange fires on next capture
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [preview, onCapture])

  const handleRetake = useCallback(() => {
    setPreview(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
        <div>
          <h3 className="font-semibold text-slate-900">Capture Photo</h3>
          {selectedCategory && (
            <p className="text-sm text-slate-500">
              Category: <span className="font-medium text-primary-700">{selectedCategory.name}</span>
            </p>
          )}
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Camera/Preview Area */}
      <div className="aspect-[4/3] bg-slate-900 relative flex items-center justify-center">
        {loading ? (
          <div className="flex flex-col items-center gap-4">
            <div className="spinner w-12 h-12 border-4" />
            <p className="text-white">
              {multiProgress
                ? `Processing ${multiProgress.current}/${multiProgress.total}...`
                : 'Processing image...'}
            </p>
          </div>
        ) : preview ? (
          <img
            src={preview.imageData}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="flex flex-col items-center gap-4 text-slate-400">
            <Camera className="w-20 h-20" />
            <p className="text-lg">Tap to capture photo</p>
          </div>
        )}

        {/* Hidden file input for camera */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Hidden file input for gallery (multi-select) */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleGallerySelect}
          className="hidden"
        />

        {/* Tap to capture overlay (when no preview) */}
        {!preview && !loading && (
          <button
            onClick={triggerFileInput}
            className="absolute inset-0 w-full h-full"
            aria-label="Capture photo"
          />
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 border-t border-red-200">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="p-4 bg-slate-50 border-t border-slate-200">
        {preview ? (
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleRetake}
              icon="RotateCcw"
              className="flex-1"
            >
              Retake
            </Button>
            <Button
              variant="primary"
              onClick={handleCapture}
              icon="Check"
              className="flex-1"
            >
              Use Photo
            </Button>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={triggerFileInput}
              icon="Camera"
              className="flex-1"
              disabled={loading}
            >
              Take Photo
            </Button>
          </div>
        )}
      </div>

      {/* Alternative: Select from gallery (supports multi-select) */}
      {!preview && !loading && (
        <div className="p-4 pt-0">
          <button
            onClick={() => galleryInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-3 text-slate-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <ImagePlus className="w-5 h-5" />
            <span>Or select from gallery</span>
          </button>
        </div>
      )}
    </div>
  )
}

// Compact camera button for quick capture
export function QuickCaptureButton({ onCapture, category, disabled = false }) {
  const fileInputRef = useRef(null)
  const [loading, setLoading] = useState(false)

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setLoading(true)
    try {
      const compressedBlob = await compressImage(file)
      const thumbnailBlob = await createThumbnailBlob(compressedBlob)
      const imageData = await blobToBase64(compressedBlob)
      const thumbnail = await blobToBase64(thumbnailBlob)

      onCapture({
        imageData,
        thumbnail,
        blob: compressedBlob,
        thumbnailBlob,
        categoryCode: category?.code
      })
    } catch (err) {
      console.error('Failed to process image:', err)
    } finally {
      setLoading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <Button
        variant="primary"
        size="lg"
        icon="Camera"
        onClick={() => fileInputRef.current?.click()}
        loading={loading}
        disabled={disabled || !category}
        className="shadow-lg"
      >
        {loading ? 'Processing...' : 'Capture Photo'}
      </Button>
    </>
  )
}
